import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { clearAuthSession, getStoredUser, isAuthenticated, setStoredUser } from '../../utils/auth';
import { getProfile, unlockVideosMenu } from '../../services/api';

export type UserShape = { fullName?: string; [k: string]: any };

const readUser = (): UserShape => getStoredUser<UserShape>() || { fullName: 'User' };
const readToken = () => isAuthenticated();

/**
 * Carries over the auth-sync/avatar-menu/video-unlock logic that previously
 * lived inline in components/Layout.tsx, so Sidebar/Topbar/MobileNavDrawer
 * can share one source of truth instead of re-deriving it.
 */
export const useAuthShell = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<UserShape>(readUser());
  const [isAuthed, setIsAuthed] = useState<boolean>(readToken());

  useEffect(() => {
    const sync = () => {
      setUser(readUser());
      setIsAuthed(readToken());
    };
    const syncOnVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };

    window.addEventListener('storage', sync);
    window.addEventListener('user-updated', sync);
    window.addEventListener('auth-changed', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', syncOnVisible);

    const intervalId = window.setInterval(sync, 60_000);

    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('user-updated', sync);
      window.removeEventListener('auth-changed', sync);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', syncOnVisible);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const needsFetch = isAuthed && (!user || !user.fullName || user.fullName === 'User');
    if (!needsFetch) return;
    (async () => {
      try {
        const prof = await getProfile();
        setStoredUser(prof);
        setUser(prof);
      } catch {
        // invalid token — leave to 401 interceptor
      }
    })();
  }, [isAuthed, user]);

  const handleLogout = () => {
    clearAuthSession();
    setIsAuthed(false);
    setUser({ fullName: 'User' });
    navigate('/login');
  };

  const openVideos = async () => {
    const result = await Swal.fire({
      title: 'Unlock Videos',
      text: 'Enter your login password to open the videos section.',
      icon: 'warning',
      input: 'password',
      inputLabel: 'Login password',
      inputPlaceholder: 'Enter your password',
      confirmButtonText: 'Unlock',
      showCancelButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      preConfirm: async (value) => {
        const pwd = String(value || '').trim();
        if (!pwd) {
          Swal.showValidationMessage('Password is required');
          return false;
        }
        try {
          await unlockVideosMenu(pwd);
          return true;
        } catch (e: any) {
          Swal.showValidationMessage(e?.response?.data?.message || 'Incorrect password');
          return false;
        }
      },
    });

    if (!result.isConfirmed) return;

    navigate('/videos', {
      state: { videoAccessGranted: true, fromPath: location.pathname },
    });
  };

  const go = (path: string) => {
    if (path === '/videos') {
      void openVideos();
      return;
    }
    navigate(path);
  };

  const avatarInitial = useMemo(
    () => (user?.fullName && user.fullName.trim().charAt(0).toUpperCase()) || 'U',
    [user],
  );

  return { user, isAuthed, avatarInitial, handleLogout, openVideos, go, active: location.pathname };
};
